import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Avatar, Spinner } from 'components/elements';
import ChatGroupModal from 'components/elements/chatGroupModal';
import ContactModal from 'components/elements/chatGroupModal/ContactModal';
import JoinGroupModal from 'components/elements/chatGroupModal/JoinGroupModal';
import { Button, Dropdown, Input, message } from 'antd';
import type { MenuProps } from 'antd';
import { SearchOutlined, TeamOutlined, UserAddOutlined } from '@ant-design/icons';
import { useChat } from './context/ChatContext';
import { getMessageThreadPath } from './messageUrl';
import type { Conversation } from './types';
import {
  formatConversationTime,
  formatLastMessagePreview,
  getConversationTitle,
} from './conversationListUtils';
import {
  ChatConversationAvatarWrap,
  ChatConversationBody,
  ChatConversationBadgeSlot,
  ChatConversationGroupIcon,
  ChatConversationItem,
  ChatConversationList,
  ChatConversationName,
  ChatConversationNameRow,
  ChatConversationPreview,
  ChatConversationTime,
  ChatEmptyState,
  ChatLoadingState,
  ChatLoadingText,
  ChatSidebar,
  ChatSidebarHeader,
  ChatSidebarSearch,
  ChatSidebarTitle,
  ChatUnreadBadge,
} from './chat.styled';

interface SidebarProps {
  visibleOnMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ visibleOnMobile = false }) => {
  const history = useHistory();
  const {
    user,
    availableUsers,
    conversations,
    conversationsLoading,
    creatingConversation,
    updatingConversation,
    activeConversation,
    handleStartPrivateChat,
    handleCreateGroup,
    handleJoinChannel,
  } = useChat();

  const [searchQuery, setSearchQuery] = useState('');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [joinGroupModalOpen, setJoinGroupModalOpen] = useState(false);

  const filteredConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const title = getConversationTitle(c).toLowerCase();
      const preview = formatLastMessagePreview(c, user?.id).toLowerCase();
      return title.includes(q) || preview.includes(q);
    });
  }, [conversations, searchQuery, user]);

  const hasSearchQuery = searchQuery.trim().length > 0;

  const createMenuItems: MenuProps['items'] = [
    { key: 'new-group', icon: <TeamOutlined />, label: 'New Group' },
    { key: 'join-group', icon: <TeamOutlined />, label: 'Join Group' },
    { type: 'divider' },
    { key: 'new-contact', icon: <UserAddOutlined />, label: 'New Contact' },
  ];

  const onCreateMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'new-group') setGroupModalOpen(true);
    if (key === 'join-group') setJoinGroupModalOpen(true);
    if (key === 'new-contact') setContactModalOpen(true);
  };

  const renderConversationAvatar = (conversation: Conversation) => {
    return (
      <ChatConversationAvatarWrap>
        <Avatar
          avatarUrl={conversation.is_group ? undefined : conversation.other_avatar_url ?? undefined}
          size="xsm"
        />
      </ChatConversationAvatarWrap>
    );
  };

  const renderConversationName = (conversation: Conversation, title: string) => (
    <ChatConversationNameRow>
      {conversation.is_group ? (
        <ChatConversationGroupIcon>
          <TeamOutlined />
        </ChatConversationGroupIcon>
      ) : null}
      <ChatConversationName>{title}</ChatConversationName>
    </ChatConversationNameRow>
  );

  return (
    <ChatSidebar $visibleOnMobile={visibleOnMobile}>
      <ChatSidebarHeader>
        <ChatSidebarTitle>Chats</ChatSidebarTitle>
        <Dropdown
          placement="bottomRight"
          trigger={['click']}
          menu={{ items: createMenuItems, onClick: onCreateMenuClick }}
        >
          <Button type="primary">New Contact/Group</Button>
        </Dropdown>
      </ChatSidebarHeader>

      <ChatSidebarSearch>
        <Input
          allowClear
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#98a2b3' }} />}
          aria-label="Search"
        />
      </ChatSidebarSearch>

      <ChatConversationList>
        {conversationsLoading && conversations.length === 0 && (
          <ChatLoadingState>
            <Spinner size="md" />
            <ChatLoadingText>Loading contacts...</ChatLoadingText>
          </ChatLoadingState>
        )}
        {filteredConversations.map((c) => {
          const isActive = activeConversation?.id === c.id;
          const unread = c.unread_count ?? 0;
          const title = getConversationTitle(c);
          const preview = formatLastMessagePreview(c, user?.id);
          const timeLabel = formatConversationTime(c.last_message_at ?? c.updated_at);

          return (
            <ChatConversationItem
              key={c.id}
              $active={isActive}
              onClick={() => history.push(getMessageThreadPath(c))}
            >
              {renderConversationAvatar(c)}
              <ChatConversationBody>
                {renderConversationName(c, title)}
                {timeLabel ? <ChatConversationTime>{timeLabel}</ChatConversationTime> : null}
                <ChatConversationPreview $unread={unread > 0}>{preview}</ChatConversationPreview>
                <ChatConversationBadgeSlot>
                  {unread > 0 ? (
                    <ChatUnreadBadge>
                      {unread > 99 ? '99+' : unread}
                    </ChatUnreadBadge>
                  ) : null}
                </ChatConversationBadgeSlot>
              </ChatConversationBody>
            </ChatConversationItem>
          );
        })}
        {!conversationsLoading && conversations.length === 0 && (
          <ChatEmptyState>No conversations yet.</ChatEmptyState>
        )}
        {!conversationsLoading && conversations.length > 0 && hasSearchQuery && filteredConversations.length === 0 && (
          <ChatEmptyState>No chats match your search.</ChatEmptyState>
        )}
      </ChatConversationList>

      <ContactModal
        open={contactModalOpen}
        onCancel={() => setContactModalOpen(false)}
        onConfirm={async ({ userId, username }) => {
          await handleStartPrivateChat({ userId, username });
          setContactModalOpen(false);
        }}
        loading={creatingConversation}
        users={availableUsers}
        currentUserId={user ? String(user.id) : undefined}
      />

      <ChatGroupModal
        modalVisible={groupModalOpen}
        setModal={setGroupModalOpen}
        contacts={availableUsers.map((entry) => ({
          id: entry.id,
          username: entry.username,
          avatarUrl: entry.avatarUrl,
          group: 0,
        }))}
        onCreateGroup={async (payload) => {
          await handleCreateGroup({
            name: payload.name,
            members: payload.members,
          });
          setGroupModalOpen(false);
        }}
      />

      <JoinGroupModal
        open={joinGroupModalOpen}
        loading={updatingConversation}
        onCancel={() => setJoinGroupModalOpen(false)}
        onJoin={async ({ groupId }) => {
          try {
            await handleJoinChannel({ groupId });
            message.success('Joined group successfully');
            setJoinGroupModalOpen(false);
          } catch (error) {
            if (error instanceof Error) {
              message.error(error.message);
            } else {
              message.error('Failed to join group');
            }
          }
        }}
      />
    </ChatSidebar>
  );
};

export default Sidebar;
